var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { CrudQueryBuilder, GraphQlBuilder } from "@contember/client";
import { BindingError } from "../BindingError.js";
import { PRIMARY_KEY_NAME, TYPENAME_KEY_NAME } from "../bindingTypes.js";
import { QueryGenerator } from "./QueryGenerator.js";
import { isEmptyObject } from "../utils/isEmptyObject.js";
import { ClientGeneratedUuid, ServerId } from "../accessorTree/RuntimeId.js";
import { assertNever } from "../utils/assertNever.js";
import { getEntityMarker } from "./state/getEntityMarker.js";
import { StateIterator } from "./state/StateIterator.js";
import { HasManyRelationMarker } from "../markers/HasManyRelationMarker.js";
import { HasOneRelationMarker } from "../markers/HasOneRelationMarker.js";
import { MutationAlias } from "./requestAliases/MutationAlias.js";
class MutationGenerator {
  constructor(treeStore) {
    __publicField(this, "aliasCounter", 1);
    this.treeStore = treeStore;
  }
  getPersistMutation() {
    try {
      let builder = new CrudQueryBuilder.CrudQueryBuilder();
      const operations = [];
      const processedPlaceholdersByEntity = /* @__PURE__ */ new Map();
      const processedDeletes = /* @__PURE__ */ new Set();
      for (const [treeRootId, rootStates] of this.treeStore.subTreeStatesByRoot.entries()) {
        for (const [placeholderName, subTreeState] of Array.from(rootStates.entries()).reverse()) {
          if (subTreeState.unpersistedChangesCount <= 0) {
            continue;
          }
          if (subTreeState.type === "entityRealm") {
            const subMutation = this.addSubMutation(
              processedPlaceholdersByEntity,
              placeholderName,
              "single",
              subTreeState.entity.id.value,
              subTreeState,
              builder
            );
            if (subMutation) {
              builder = subMutation[0];
              operations.push(subMutation[1]);
            }
          } else if (subTreeState.type === "entityList") {
            for (const childState of subTreeState.children.values()) {
              if (childState.type === "entityRealmStub") {
                continue;
              }
              const subMutation = this.addSubMutation(
                processedPlaceholdersByEntity,
                placeholderName,
                "list",
                childState.entity.id.value,
                childState,
                builder
              );
              if (subMutation) {
                builder = subMutation[0];
                operations.push(subMutation[1]);
              }
            }
            if (subTreeState.plannedRemovals) {
              for (const [removedId, removalType] of subTreeState.plannedRemovals) {
                if (removalType === "disconnect") {
                  continue;
                }
                if (removalType === "delete") {
                  const key = `${subTreeState.entityName}#${removedId}`;
                  if (processedDeletes.has(key)) {
                    continue;
                  }
                  processedDeletes.add(key);
                  const alias = this.createAlias();
                  builder = this.addDeleteMutation(
                    subTreeState.entityName,
                    removedId,
                    alias,
                    builder
                  );
                  operations.push({ alias, subTreePlaceholder: placeholderName, subTreeType: "list", type: "delete", id: removedId });
                } else {
                  assertNever(removalType);
                }
              }
            }
          } else {
            assertNever(subTreeState);
          }
        }
      }
      const query = builder.inTransaction(void 0, { deferForeignKeyConstraints: true }).getGql();
      return { query, operations };
    } catch (e) {
      return void 0;
    }
  }
  addSubMutation(processedPlaceholdersByEntity, subTreePlaceholder, subTreeType, entityId, realmState, queryBuilder) {
    if (realmState.unpersistedChangesCount === 0) {
      return void 0;
    }
    const fieldMarkers = getEntityMarker(realmState).fields.markers;
    const alias = this.createAlias();
    if (realmState.entity.isScheduledForDeletion) {
      const builder2 = this.addDeleteMutation(
        realmState.entity.entityName,
        entityId,
        alias,
        queryBuilder
      );
      return [
        builder2,
        {
          alias,
          subTreePlaceholder,
          subTreeType,
          type: "delete",
          id: entityId
        }
      ];
    } else if (!realmState.entity.id.existsOnServer) {
      const builder2 = this.addCreateMutation(
        processedPlaceholdersByEntity,
        realmState,
        alias,
        this.getNodeFragmentName(subTreePlaceholder, subTreeType, realmState),
        queryBuilder,
        fieldMarkers
      );
      if (!builder2) {
        return void 0;
      }
      return [
        builder2,
        {
          alias,
          subTreePlaceholder,
          subTreeType,
          type: "create",
          markers: fieldMarkers,
          id: entityId
        }
      ];
    }
    const builder = this.addUpdateMutation(
      processedPlaceholdersByEntity,
      realmState,
      alias,
      this.getNodeFragmentName(subTreePlaceholder, subTreeType, realmState),
      queryBuilder,
      fieldMarkers
    );
    if (!builder) {
      return void 0;
    }
    return [
      builder,
      {
        alias,
        subTreePlaceholder,
        subTreeType,
        type: "update",
        id: entityId,
        markers: fieldMarkers
      }
    ];
  }
  getNodeFragmentName(subTreePlaceholder, subTreeType, realmState) {
    if (subTreeType !== "list") {
      return void 0;
    }
    return `${realmState.entity.entityName}_${subTreePlaceholder}`;
  }
  addDeleteMutation(entityName, deletedId, alias, queryBuilder = new CrudQueryBuilder.CrudQueryBuilder()) {
    return queryBuilder.delete(
      entityName,
      (builder) => builder.ok().node((builder2) => builder2.column(PRIMARY_KEY_NAME)).by({ [PRIMARY_KEY_NAME]: deletedId }).errors().errorMessage(),
      alias
    );
  }
  addUpdateMutation(processedPlaceholdersByEntity, entityRealm, alias, nodeFragmentName, queryBuilder = new CrudQueryBuilder.CrudQueryBuilder(), fieldMarkers) {
    const updateBuilder = this.registerUpdateMutationPart(
      processedPlaceholdersByEntity,
      entityRealm,
      new CrudQueryBuilder.WriteDataBuilder()
    );
    if (updateBuilder.data === void 0 || isEmptyObject(updateBuilder.data)) {
      return void 0;
    }
    const runtimeId = entityRealm.entity.id;
    if (!runtimeId.existsOnServer) {
      return void 0;
    }
    const readBuilder = QueryGenerator.registerQueryPart(
      fieldMarkers,
      CrudQueryBuilder.ReadBuilder.instantiate()
    );
    if (nodeFragmentName) {
      queryBuilder = queryBuilder.fragment(nodeFragmentName, entityRealm.entity.entityName, readBuilder);
    }
    return queryBuilder.update(
      entityRealm.entity.entityName,
      (builder) => builder.node(nodeFragmentName ? (builder2) => builder2.applyFragment(nodeFragmentName) : readBuilder).data(updateBuilder).by({ [PRIMARY_KEY_NAME]: runtimeId.value }).ok().validation().errors().errorMessage(),
      alias
    );
  }
  addCreateMutation(processedPlaceholdersByEntity, entityRealm, alias, nodeFragmentName, queryBuilder = new CrudQueryBuilder.CrudQueryBuilder(), fieldMarkers) {
    const createBuilder = this.registerCreateMutationPart(
      processedPlaceholdersByEntity,
      entityRealm,
      new CrudQueryBuilder.WriteDataBuilder()
    );
    if (createBuilder.data === void 0 || isEmptyObject(createBuilder.data)) {
      return void 0;
    }
    const readBuilder = QueryGenerator.registerQueryPart(
      fieldMarkers,
      CrudQueryBuilder.ReadBuilder.instantiate()
    );
    if (nodeFragmentName) {
      queryBuilder = queryBuilder.fragment(nodeFragmentName, entityRealm.entity.entityName, readBuilder);
    }
    return queryBuilder.create(
      entityRealm.entity.entityName,
      (builder) => {
        return builder.node(nodeFragmentName ? (builder2) => builder2.applyFragment(nodeFragmentName) : readBuilder).data(createBuilder).ok().validation().errors().errorMessage();
      },
      alias
    );
  }
  registerCreateMutationPart(processedPlaceholdersByEntity, currentState, builder = new CrudQueryBuilder.WriteDataBuilder()) {
    let processedPlaceholders = processedPlaceholdersByEntity.get(currentState.entity);
    if (processedPlaceholders === void 0) {
      processedPlaceholdersByEntity.set(currentState.entity, processedPlaceholders = /* @__PURE__ */ new Set());
    }
    if (currentState.type === "entityRealmStub") {
      return builder;
    }
    for (const siblingState of StateIterator.eachSiblingRealm(currentState)) {
      const pathBack = this.treeStore.getPathBackToParent(siblingState);
      const nonbearingFields = [];
      for (const [placeholderName, fieldState] of siblingState.children) {
        if (processedPlaceholders.has(placeholderName)) {
          continue;
        }
        processedPlaceholders.add(placeholderName);
        switch (fieldState.type) {
          case "field": {
            const marker = fieldState.fieldMarker;
            const placeholderName2 = marker.placeholderName;
            if (placeholderName2 === TYPENAME_KEY_NAME || placeholderName2 === PRIMARY_KEY_NAME && !(siblingState.entity.id instanceof ClientGeneratedUuid)) {
              continue;
            }
            if (marker.isNonbearing) {
              nonbearingFields.push({
                type: "field",
                marker,
                fieldState
              });
              continue;
            }
            builder = this.registerCreateFieldPart(fieldState, marker, builder);
            break;
          }
          case "entityRealmStub":
          case "entityRealm": {
            const marker = getEntityMarker(fieldState);
            if (!(marker instanceof HasOneRelationMarker)) {
              throw new BindingError();
            }
            if (pathBack?.fieldBackToParent === marker.parameters.field) {
              continue;
            }
            if (marker.parameters.isNonbearing) {
              nonbearingFields.push({
                type: "hasOne",
                marker,
                fieldState
              });
              continue;
            }
            builder = this.registerCreateEntityPart(processedPlaceholdersByEntity, fieldState, marker, builder);
            break;
          }
          case "entityList": {
            const marker = fieldState.blueprint.marker;
            if (!(marker instanceof HasManyRelationMarker)) {
              throw new BindingError();
            }
            if (pathBack?.fieldBackToParent === marker.parameters.field) {
              continue;
            }
            if (marker.parameters.isNonbearing) {
              nonbearingFields.push({
                type: "hasMany",
                marker,
                fieldState
              });
              continue;
            }
            builder = this.registerCreateEntityListPart(processedPlaceholdersByEntity, fieldState, marker, builder);
            break;
          }
          default: {
            return assertNever();
          }
        }
      }
      if (builder.data !== void 0 && !isEmptyObject(builder.data) || !getEntityMarker(siblingState).fields.hasAtLeastOneBearingField) {
        for (const field of nonbearingFields) {
          switch (field.type) {
            case "field": {
              builder = this.registerCreateFieldPart(field.fieldState, field.marker, builder);
              break;
            }
            case "hasOne": {
              builder = this.registerCreateEntityPart(
                processedPlaceholdersByEntity,
                field.fieldState,
                field.marker,
                builder
              );
              break;
            }
            case "hasMany": {
              builder = this.registerCreateEntityListPart(
                processedPlaceholdersByEntity,
                field.fieldState,
                field.marker,
                builder
              );
              break;
            }
            default:
              assertNever();
          }
        }
      }
      const setOnCreate = getEntityMarker(siblingState).parameters.setOnCreate;
      if (setOnCreate && builder.data !== void 0 && !isEmptyObject(builder.data)) {
        for (const key in setOnCreate) {
          const field = setOnCreate[key];
          if (typeof field === "string" || typeof field === "number" || field === null || field instanceof GraphQlBuilder.GraphQlLiteral) {
            builder = builder.set(key, field);
          } else {
            builder = builder.one(key, (builder2) => builder2.connect(field));
          }
        }
      }
    }
    return builder;
  }
  registerCreateFieldPart(fieldState, marker, builder) {
    const resolvedValue = fieldState.getAccessor().resolvedValue;
    if (resolvedValue !== void 0 && resolvedValue !== null) {
      return builder.set(marker.fieldName, this.transformFieldValue(fieldState, resolvedValue));
    }
    return builder;
  }
  registerCreateEntityPart(processedPlaceholdersByEntity, fieldState, marker, builder) {
    const reducedBy = marker.parameters.reducedBy;
    const runtimeId = fieldState.entity.id;
    if (reducedBy === void 0) {
      return builder.one(marker.parameters.field, (builder2) => {
        if (this.shouldConnectInsteadOfCreate(processedPlaceholdersByEntity, fieldState)) {
          return builder2.connect({ [PRIMARY_KEY_NAME]: runtimeId.value });
        }
        return builder2.create(this.registerCreateMutationPart(processedPlaceholdersByEntity, fieldState));
      });
    }
    return builder.many(marker.parameters.field, (builder2) => {
      const alias = MutationAlias.encodeEntityId(runtimeId);
      if (this.shouldConnectInsteadOfCreate(processedPlaceholdersByEntity, fieldState)) {
        return builder2.connect({ [PRIMARY_KEY_NAME]: runtimeId.value }, alias);
      }
      return builder2.create(this.registerCreateMutationPart(processedPlaceholdersByEntity, fieldState), alias);
    });
  }
  registerCreateEntityListPart(processedPlaceholdersByEntity, fieldState, marker, builder) {
    return builder.many(marker.parameters.field, (builder2) => {
      for (const entityRealm of fieldState.children.values()) {
        const runtimeId = entityRealm.entity.id;
        const alias = MutationAlias.encodeEntityId(runtimeId);
        if (this.shouldConnectInsteadOfCreate(processedPlaceholdersByEntity, entityRealm)) {
          builder2 = builder2.connect({ [PRIMARY_KEY_NAME]: runtimeId.value }, alias);
        } else {
          builder2 = builder2.create(this.registerCreateMutationPart(processedPlaceholdersByEntity, entityRealm), alias);
        }
      }
      return builder2;
    });
  }
  shouldConnectInsteadOfCreate(processedPlaceholdersByEntity, currentState) {
    const runtimeId = currentState.entity.id;
    const processedPlaceholders = processedPlaceholdersByEntity.get(currentState.entity);
    return runtimeId.existsOnServer || runtimeId instanceof ClientGeneratedUuid && (processedPlaceholders?.has(PRIMARY_KEY_NAME) ?? false);
  }
  registerUpdateMutationPart(processedPlaceholdersByEntity, currentState, builder) {
    let processedPlaceholders = processedPlaceholdersByEntity.get(currentState.entity);
    if (processedPlaceholders === void 0) {
      processedPlaceholdersByEntity.set(currentState.entity, processedPlaceholders = /* @__PURE__ */ new Set());
    }
    const pathBack = this.treeStore.getPathBackToParent(currentState);
    const entityData = this.treeStore.persistedEntityData.get(currentState.entity.id.uniqueValue);
    for (const [placeholderName, fieldState] of currentState.children) {
      if (placeholderName === PRIMARY_KEY_NAME || placeholderName === TYPENAME_KEY_NAME) {
        continue;
      }
      if (processedPlaceholders.has(placeholderName)) {
        continue;
      }
      processedPlaceholders.add(placeholderName);
      switch (fieldState.type) {
        case "field": {
          if (fieldState.persistedValue !== void 0) {
            const resolvedValue = fieldState.getAccessor().resolvedValue;
            if (fieldState.persistedValue !== resolvedValue) {
              builder = builder.set(placeholderName, this.transformFieldValue(fieldState, resolvedValue));
            }
          }
          break;
        }
        case "entityRealmStub":
        case "entityRealm": {
          const marker = getEntityMarker(fieldState);
          if (!(marker instanceof HasOneRelationMarker)) {
            throw new BindingError();
          }
          if (pathBack?.fieldBackToParent === marker.parameters.field) {
            continue;
          }
          const runtimeId = fieldState.entity.id;
          const reducedBy = marker.parameters.reducedBy;
          if (reducedBy === void 0) {
            const subBuilder = ((builder2) => {
              const persistedValue = entityData?.get?.(placeholderName);
              if (persistedValue instanceof ServerId) {
                if (persistedValue.value === runtimeId.value) {
                  if (fieldState.type === "entityRealmStub") {
                    return builder2;
                  }
                  return builder2.update(
                    (builder3) => this.registerUpdateMutationPart(processedPlaceholdersByEntity, fieldState, builder3)
                  );
                }
                const plannedDeletion = currentState.plannedHasOneDeletions?.get(placeholderName);
                if (plannedDeletion !== void 0) {
                  return builder2.delete();
                }
                if (runtimeId.existsOnServer) {
                  return builder2.connect({
                    [PRIMARY_KEY_NAME]: runtimeId.value
                  });
                }
                const subBuilder2 = builder2.create(
                  this.registerCreateMutationPart(processedPlaceholdersByEntity, fieldState)
                );
                if (isEmptyObject(subBuilder2.data)) {
                  return builder2.disconnect();
                }
                return subBuilder2;
              } else if (runtimeId.existsOnServer) {
                return builder2.connect({
                  [PRIMARY_KEY_NAME]: runtimeId.value
                });
              } else {
                return builder2.create(this.registerCreateMutationPart(processedPlaceholdersByEntity, fieldState));
              }
            })(CrudQueryBuilder.WriteOneRelationBuilder.instantiate());
            if (subBuilder.data) {
              builder = builder.one(marker.parameters.field, subBuilder.data);
            }
          } else {
            builder = builder.many(marker.parameters.field, (builder2) => {
              const persistedValue = entityData?.get?.(placeholderName);
              const alias = MutationAlias.encodeEntityId(runtimeId);
              if (persistedValue instanceof ServerId) {
                if (persistedValue.value === runtimeId.value) {
                  if (fieldState.type === "entityRealmStub") {
                    return builder2;
                  }
                  return builder2.update(
                    reducedBy,
                    (builder3) => this.registerUpdateMutationPart(processedPlaceholdersByEntity, fieldState, builder3),
                    alias
                  );
                }
                const plannedDeletion = currentState.plannedHasOneDeletions?.get(placeholderName);
                if (plannedDeletion !== void 0) {
                  return builder2.delete(reducedBy, alias);
                }
                if (runtimeId.existsOnServer) {
                  return builder2.disconnect(reducedBy, alias).connect({ [PRIMARY_KEY_NAME]: runtimeId.value }, alias);
                }
                const subBuilder = builder2.create(
                  this.registerCreateMutationPart(processedPlaceholdersByEntity, fieldState)
                );
                if (isEmptyObject(subBuilder.data)) {
                  return builder2.disconnect(reducedBy, alias);
                }
                return subBuilder;
              } else if (runtimeId.existsOnServer) {
                return builder2.connect({ [PRIMARY_KEY_NAME]: runtimeId.value }, alias);
              } else {
                return builder2.create(this.registerCreateMutationPart(processedPlaceholdersByEntity, fieldState));
              }
            });
          }
          break;
        }
        case "entityList": {
          const marker = fieldState.blueprint.marker;
          if (!(marker instanceof HasManyRelationMarker)) {
            throw new BindingError();
          }
          if (pathBack?.fieldBackToParent === marker.parameters.field) {
            continue;
          }
          const persistedEntityIds = entityData?.get?.(placeholderName) ?? /* @__PURE__ */ new Set();
          if (!(persistedEntityIds instanceof Set)) {
            continue;
          }
          builder = builder.many(marker.parameters.field, (builder2) => {
            for (const childState of fieldState.children.values()) {
              const runtimeId = childState.entity.id;
              const alias = MutationAlias.encodeEntityId(runtimeId);
              if (runtimeId.existsOnServer) {
                if (persistedEntityIds.has(runtimeId.value)) {
                  if (childState.type !== "entityRealmStub") {
                    builder2 = builder2.update(
                      { [PRIMARY_KEY_NAME]: runtimeId.value },
                      (builder3) => this.registerUpdateMutationPart(processedPlaceholdersByEntity, childState, builder3),
                      alias
                    );
                  }
                } else {
                  builder2 = builder2.connect({ [PRIMARY_KEY_NAME]: runtimeId.value }, alias);
                }
              } else {
                builder2 = builder2.create(
                  this.registerCreateMutationPart(processedPlaceholdersByEntity, childState),
                  alias
                );
              }
            }
            if (fieldState.plannedRemovals) {
              for (const [removedId, removalType] of fieldState.plannedRemovals) {
                const alias = MutationAlias.encodeEntityId(new ServerId(removedId, fieldState.entityName));
                if (removalType === "delete") {
                  builder2 = builder2.delete({ [PRIMARY_KEY_NAME]: removedId }, alias);
                } else if (removalType === "disconnect") {
                  builder2 = builder2.disconnect({ [PRIMARY_KEY_NAME]: removedId }, alias);
                } else {
                  assertNever();
                }
              }
            }
            return builder2;
          });
          break;
        }
        default:
          return assertNever();
      }
    }
    return builder;
  }
  transformFieldValue(fieldState, value) {
    if (typeof value !== "string") {
      return value;
    }
    const fieldSchema = this.treeStore.schema.getEntityColumn(
      fieldState.parent.entity.entityName,
      fieldState.fieldMarker.fieldName
    );
    return fieldSchema.enumName === null ? value : new GraphQlBuilder.GraphQlLiteral(value);
  }
  createAlias() {
    return `op_${this.aliasCounter++}`;
  }
}
export {
  MutationGenerator
};
//# sourceMappingURL=MutationGenerator.js.map
